import {
  CheckCircleIcon,
  CircleNotchIcon,
  InfoIcon,
  MinusIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import { cn } from '@/lib/utils';

/**
 * Per-line discount/approval status (spec §7.3). The chip vocabulary must match
 * the approval workflow + the task card.
 */
export type LineStatus =
  'needs' | 'await' | 'queued' | 'approved' | 'special' | 'above' | 'none';

/**
 * Pill visuals ported from the v2 DiscountBadge (icon + tinted background +
 * colored label, fixed min width) so the two review pages read identically.
 * Only the pill design comes from v2 — the tooltip and stack-dialog popup
 * experience stay v3's.
 */
const STATUS: Record<
  LineStatus,
  {
    Icon: typeof CheckCircleIcon;
    iconClass: string;
    chipClass: string;
    spin?: boolean;
  }
> = {
  needs: {
    Icon: WarningIcon,
    iconClass: 'text-red-600',
    chipClass: 'bg-red-600/10 text-red-700',
  },
  await: {
    Icon: CircleNotchIcon,
    iconClass: 'text-orange-500',
    chipClass: 'bg-orange-600/10 text-orange-700',
  },
  queued: {
    Icon: CircleNotchIcon,
    iconClass: 'text-muted-foreground',
    chipClass: 'bg-foreground/8 text-muted-foreground',
  },
  approved: {
    Icon: CheckCircleIcon,
    iconClass: 'text-green-600',
    chipClass: 'bg-green-600/10 text-green-700',
  },
  special: {
    Icon: InfoIcon,
    iconClass: 'text-blue-500',
    chipClass: 'bg-blue-600/10 text-blue-700',
  },
  above: {
    Icon: MinusIcon,
    iconClass: 'text-gray-500',
    chipClass: 'bg-foreground/8 text-muted-foreground',
  },
  none: {
    Icon: MinusIcon,
    iconClass: 'text-gray-500',
    chipClass: 'bg-foreground/8 text-muted-foreground',
  },
};

export function LineStatusChip({ status }: { status: LineStatus }) {
  const t = useGT();
  const cfg = STATUS[status];
  // Literal t() calls per status (i18n extraction needs static strings).
  const label =
    status === 'needs'
      ? t('Review')
      : status === 'await'
        ? t('Pending')
        : status === 'queued'
          ? t('Queued')
          : status === 'approved'
            ? t('Approved')
            : status === 'special'
              ? t('No List Price')
              : status === 'above'
                ? t('Above List')
                : t('No Discount');
  return (
    <span
      className={cn(
        'inline-flex min-w-[110px] items-center justify-center gap-1 rounded px-2 py-1 text-xs font-medium whitespace-nowrap',
        cfg.chipClass,
      )}
    >
      <cfg.Icon size={14} className={cfg.iconClass} />
      {label}
    </span>
  );
}
